(ns frontend.view.user-management.guest
  (:require [basic-components.html-table.cell :as table-cell]
            [basic-components.html-table.table :as table]
            [common.entity :as entity]
            [common.localization :as loc]
            [common.util :as cu]
            [frontend.bootstrap.components :as rbs]
            [frontend.state.current-session :as session]
            [frontend.utils :as fu]
            [frontend.view.modals.account-edit.view :as account-edit]
            [frontend.view.modals.events :as modals]
            [frontend.view.user-management.components :as components]
            [frontend.view.user-management.events :as events]
            [reagent.core :as r]
            [reagent.ratom :as ratom]
            [ui.components.radix-ui.popover.view :as popover]
            [ui.components.toggle.view :as toggle]
            [ui.components.input.checkbox :as checkbox]))

(defn attach-scroll-handler-to-parents
  [node handler]
  (when node
    (.addEventListener node "scroll" handler)
    (recur (-> node
               .-parentElement)
           handler)))

(defn remove-scroll-handler-to-parents
  [node handler]
  (when node
    (.removeEventListener node "scroll" handler)
    (recur (-> node
               .-parentElement)
           handler)))

(defn guest-user-tools
  [{:keys [account account-role state]}]
  (let [target-id (:role/target account-role)
        person (get @session/entities (:role/person account-role))
        target-entity (get @session/entities target-id)
        email (:account/email account)
        role-kw (:role/role account-role)
        account-id (:db/id account)
        account-role-id (:db/id account-role)
        disabled-role? (:common/disabled? account-role)]
    [:div
     {:class "mb-0 flex flex-col text-sm"}
     (when-not (:common/disabled? account-role)
       [:span
        {:class-name (fu/cs "min-w-48 mb-0 p-2 flex items-center gap-3
                                   text-sm text-base-light-text
                                   hover:bg-base-regular !outline-0 shadow-none
                                   rounded
                                   cursor-pointer")
         :on-click   (fn [_]
                       (modals/open-modal! account-edit/modal-key
                                           {:account-id      account-id
                                            :role-id         account-role-id
                                            :title           (loc/tr :edit-user-details)
                                            :submit-button-text (loc/tr :save)
                                            :success-message (loc/tr :success-account-modified)}))}
        [:i.fal.fa-pen]
        (loc/tr :settings/edit-account)])

     (when-not (:common/disabled? account-role)
       [:span
        {:class-name (fu/cs "min-w-48 mb-0 p-2 flex items-center gap-3
                                   text-sm text-base-light-text
                                   hover:bg-base-regular !outline-0 shadow-none
                                   rounded
                                   cursor-pointer")
         :on-click   #(events/re-invite-guest! {:target-id    target-id
                                                :role-id      (:db/id account-role)
                                                :account-name (entity/display-string account)
                                                :on-submit!   identity})}
        [:i.fal.fa-paper-plane]
        (loc/tr :settings/re-send-invitation)])

     (when-not (or disabled-role?
                   (= account-id @session/account-id))
       [:span
        {:class-name (fu/cs "min-w-48 mb-0 p-2 flex items-center gap-3
                                   text-sm text-base-light-text
                                   hover:bg-base-regular !outline-0 shadow-none
                                   rounded
                                   cursor-pointer")
         :on-click   #(events/deactivate-guest! account-role target-entity state)}
        [:i.fal.fa-user-times]
        (loc/tr :settings/deactivate)])
     (when disabled-role?
       [:span
        {:class "flex items-center gap-2 hover:text-base-light-text-accent cursor-pointer"
         :on-click   #(events/invite-guest! {:target-id  target-id
                                             :person     person
                                             :email      email
                                             :role-kw    role-kw
                                             :on-submit! identity})}
        [:i.fal.fa-user-plus.mr-2]
        (loc/tr :settings/reactivate)])]))

(defn checkbox-cell-renderer
  [_]
  (let [ref (atom nil)
        local-state (r/atom {})
        scroll-handler (fn []
                         (swap! local-state assoc :selected? false))
        init! (fn [r]
                (let [existing-ref @ref]
                  (when (and existing-ref
                             (not= existing-ref r))
                    (remove-scroll-handler-to-parents r scroll-handler))
                  (when (not= existing-ref r)
                    (attach-scroll-handler-to-parents r scroll-handler)
                    (reset! ref r))))]
    (r/create-class
     {:component-will-unmount
      (fn [_]
        (when-let [dom-node @ref]
          (remove-scroll-handler-to-parents dom-node scroll-handler)))
      :reagent-render
      (fn [_]
        (fn [{:keys [column-meta cell-value row-data state]}]
          (let [role-id (:role/id row-data)
                role (get @session/entities role-id)
                account-id (:role/subject role)
                account (get @session/entities account-id)
                selected? (boolean (:selected? @local-state))]

            [:div
             {:class "flex gap-3"
              :ref (fn [r]
                     (when r
                       (init! r)))}
             
             [popover/view
              {:open?          (:selected? @local-state)
               :on-open-change #(swap! local-state assoc :selected? (not selected?))
               :as-child?      true
               :positions ["bottom"]
               :align "start"}
              
              [checkbox/view
               {:value     selected?
                :on-change (fn [_e _checked?]
                             (swap! local-state assoc :selected? (not selected?)))}]
              [:div
               [guest-user-tools
                {:account      account
                 :account-role role
                 :state        state}]]]
             [table-cell/cell
              {:column-meta   column-meta
               :cell-value    cell-value
               :tenant-schema @session/tenant-schema
               :row-data      row-data
               :state         state}]])))})))

(defn resolve-bsg-columns
  [_tenant state]
  (let [tenant-schema @session/tenant-schema]
    (concat [{:column/key           :account/full-name
              :column/label         (loc/tr :account/full-name)
              :column/class-name    "sticky-col"
              :column/cell-renderer (partial checkbox-cell-renderer)
              :column/group         0
              :column/sortable?     true}
             (events/field-column tenant-schema
                                  :account/email
                                  {:column/group     1
                                   :column/sortable? true})]
            (when (:show-deactivated-guests? @state)
              [{:column/key       :settings/active?
                :column/label     (loc/tr :settings/active?)
                :column/data-type :data.type/keyword
                :column/group     1
                :column/sortable? true}])
            [(events/field-column tenant-schema
                                  :oppo/name
                                  {:column/group     1
                                   :column/sortable? true})
             (events/field-column
              tenant-schema
              :common/created-by
              {:column/group     1
               :column/sort-fn   (fn [v]
                                   (-> (get @session/entities v)
                                       (entity/resolve-entity-name session/get-entity
                                                                   session/reverse-lookup)
                                       (cu/str-trim-lcase)))
               :column/sortable? true})])))

(defn resolve-ssg-columns
  [_tenant state]
  (let [tenant-schema @session/tenant-schema]
    (concat [{:column/key           :account/full-name
              :column/label         (loc/tr :account/full-name)
              :column/class-name    "sticky-col"
              :column/cell-renderer (partial checkbox-cell-renderer)
              :column/group         0
              :column/sortable?     true}
             (events/field-column tenant-schema
                                  :account/email
                                  {:column/group     1
                                   :column/sortable? true})]
            (when (:show-deactivated-guests? @state)
              [{:column/key       :settings/active?
                :column/label     (loc/tr :settings/active?)
                :column/data-type :data.type/keyword
                :column/group     1
                :column/sortable? true}])
            [(events/field-column tenant-schema
                                  :asset/name
                                  {:column/group     1
                                   :column/sortable? true})

             (events/field-column
              tenant-schema
              :common/created-by
              {:column/group     1
               :column/sort-fn   (fn [v]
                                   (-> (get @session/entities v)
                                       (entity/resolve-entity-name session/get-entity
                                                                   session/reverse-lookup)
                                       (cu/str-trim-lcase)))
               :column/sortable? true})])))

(defn resolve-lpg-columns
  [_tenant state]
  (let [tenant-schema @session/tenant-schema]
    (concat [{:column/key           :account/full-name
              :column/label         (loc/tr :account/full-name)
              :column/class-name    "sticky-col"
              :column/cell-renderer (partial checkbox-cell-renderer)
              :column/group         0
              :column/sortable?     true}
             (events/field-column tenant-schema
                                  :account/email
                                  {:column/group     1
                                   :column/sortable? true})]
            (when (:show-deactivated-guests? @state)
              [{:column/key       :settings/active?
                :column/label     (loc/tr :settings/active?)
                :column/data-type :data.type/keyword
                :column/group     1
                :column/sortable? true}])
            [;; TODO - show all funds where reporting contact
             #_(events/field-column tenant-schema
                                    :asset/name
                                    {:column/group     1
                                     :column/sortable? true})

             (events/field-column
              tenant-schema
              :common/created-by
              {:column/group     1
               :column/sort-fn   (fn [v]
                                   (-> (get @session/entities v)
                                       (entity/resolve-entity-name session/get-entity
                                                                   session/reverse-lookup)
                                       (cu/str-trim-lcase)))
               :column/sortable? true})])))


(defn resolve-row-data
  ([role] (resolve-row-data role false))
  ([role selected?]
   (let [account (get @session/entities (:role/subject role))
         target-entity (get @session/entities (:role/target role))
         entity-type (:entity/type target-entity)
         system-account-id (->> @session/all-accounts
                                (filter (fn [account]
                                          (= (:account/first-name account) "system")))
                                (first)
                                :db/id)]
     (merge account
            {:account/full-name (entity/display-string account)
             :settings/active?  (if (:common/disabled? role)
                                  :team-member.status/deactivated
                                  :team-member.status/active)
             :role/id           (:db/id role)
             :row-selected?     selected?}
            (when (= entity-type :entity.type/oppo)
              {:oppo/name (:oppo/name target-entity)})
            (when (= entity-type :entity.type/company)
              {:asset/name (:asset/name target-entity)})
            (when-not (:common/created-by account)
              {:common/created-by system-account-id})))))

(defn advertisement
  []
  [:div
   {:class "p-5 mx-auto flex flex-col gap-4 text-center lg:w-[800px]"}
   [:div
    {:class ""} 
    [:img
     {:src        "/img/guest-feature-is-turned-off.svg"
      :class-name "max-w-64 mx-auto"}]]
   [:div
    {:class "text-xl text-semibold"}
    (loc/tr :guest-feature-title)]
   [:p
    {:class "text-base-light-text"}
    (loc/tr :guest-feature-body-1)]
   [:p
    {:class "text-base-light-text"}
    (loc/tr :guest-feature-body-2)]
   [:p
    {:class "text-base-light-text"}
    (loc/tr :guest-feature-body-3) 
    [:a 
     {:href   "mailto:support@zapflow.com"
      :target "_blank"}
     "contact person"]]])

(defn guest-results
  [{:keys [feature-kw title-kw state filtered-guest-accounts guest-accounts columns
           buy-side-guests sell-side-guests lp-guests]}]
  (let [expanded? (get @state :expanded?)
        filtered-guest-accounts @filtered-guest-accounts
        guest-accounts @guest-accounts
        lp-reporting-users? (= title-kw :lp-reporting-users)]
    (when (session/has-feature-access? feature-kw)
      [:div
       {:class "flex flex-col gap-3"}
       [components/table-toggle-title
        {:expanded? expanded?
         :title     (loc/tr title-kw [(count filtered-guest-accounts)])
         :toggle-expanded! (fn []
                             (swap! state update :expanded? not))}]
       [rbs/Collapse
        {:in expanded?}
        [:div
         {:class "border border-solid border-base-light-border-paler rounded-lg overflow-x-auto"}
         (cond
           (empty? guest-accounts)
           [components/not-guests-invited
            {:has-buy-side-guests?  (seq buy-side-guests)
             :has-sell-side-guests? (seq sell-side-guests)
             :has-lp-guests?        (seq lp-guests)
             :lp-reporting-users?   lp-reporting-users?}]

           (empty? filtered-guest-accounts)
           [components/not-found-view]

           :else
           [table/view
            filtered-guest-accounts
            {:id-key    :role/id
             :table-key "bsg-guest-accounts"
             :columns   columns
             :state     state
             :settings  {:show-selection?     true
                         :show-filters?       true
                         :show-grouping?      true
                         :show-sorting?       true
                         :default-sort-key    :common/created-on
                         :column-order        [:db/id :entity/type :common/created-on]
                         :sticky-column-group 0}}])]]])))

(defn guest-accounts-signal
  [roles-state _selection-state]
  (ratom/reaction
   (->> @roles-state
        (remove (fn [r]
                  (empty? (get @session/entities (:role/target r)))))
        (map resolve-row-data)
        (doall))))

(defn filtered-accounts-signal
  [accounts-state search-state]
  (ratom/reaction (->> @accounts-state
                       (events/filter-accounts-by-search search-state (:guest-search @search-state))
                       (events/filter-by-status search-state))))

(defn view
  [state]
  (let [tenant @session/tenant
        bsg-state (r/cursor state [:bsg-state])
        ssg-state (r/cursor state [:ssg-state])
        bsg-accounts (guest-accounts-signal session/bsg-roles bsg-state)
        ssg-accounts (guest-accounts-signal session/ssg-roles ssg-state)
        ;lpg-accounts (guest-accounts-signal session/lpg-roles lpg-state)
        filtered-bsg-accounts (filtered-accounts-signal bsg-accounts state)
        filtered-ssg-accounts (filtered-accounts-signal ssg-accounts state)
        ;filtered-lpg-accounts (filtered-accounts-signal lpg-accounts state)
        collapse-if-empty! (fn [table-state filtered-accounts-state]
                             (if (empty? @filtered-accounts-state)
                               (swap! table-state dissoc :expanded?)
                               (swap! table-state assoc :expanded? true)))
        update-search-term! (fn [value]
                              (swap! state assoc :guest-search value)
                              (fu/timeout 10                ;; allow time for reactions updates
                                          (fn []
                                            (collapse-if-empty! bsg-state filtered-bsg-accounts)
                                            (collapse-if-empty! ssg-state filtered-ssg-accounts))))]
    (swap! state assoc :selected-tab :settings/guest-accounts)
    (update-search-term! "")
    (fn [state]
      [:div
       {:class "p-4 flex flex-col gap-4"}
       [:div
        {:class "flex items-start gap-2 text-base-light-text-paler"}
        [:i
         {:class "pt-1 fas fa-info-circle"}]
        [:span (loc/tr :settings/guest-accounts-notification)]]
       [:div
        {:class "flex items-center gap-4"}
        [:div
         {:class "min-w-72 relative"}
         [components/search-bar (:guest-search @state) update-search-term! state]
         [:i
          {:class "fal fa-search absolute top-2/4 -translate-y-2/4 right-3"}]]
        [:div
         {:class "flex items-center gap-3"}
         [toggle/toggle
          {:checked?      (:show-deactivated-guests? @state)
           :on-change   (fn [toggled?]
                          (swap! state assoc :show-deactivated-guests? toggled?))}] 
         [:span (loc/tr :show-deactivated-users)]]]
       (cond
         (and (empty? @ssg-accounts)
              (empty? @bsg-accounts)
              ;(empty? @lpg-accounts)
              )
         [components/not-guests-invited
          {:has-buy-side-guests?  (seq @bsg-accounts)
           :has-sell-side-guests? (seq @ssg-accounts)
           ;:has-lp-guests?        (seq @lpg-accounts)
           }]

         (and (empty? @filtered-ssg-accounts)
              (empty? @filtered-bsg-accounts)
              ;(empty? @filtered-lpg-accounts)
              )
         [components/not-found-view]

         :else
         [:<>
          [guest-results
           {:feature-kw       :feature/buy-side-guest
            :state            bsg-state
            :filtered-guest-accounts filtered-bsg-accounts
            :title-kw         :deal-workspace-users
            :guest-accounts   bsg-accounts
            :columns          (resolve-bsg-columns tenant state)
            :buy-side-guests  @bsg-accounts
            :sell-side-guests @ssg-accounts
            ;:lp-guests        @lpg-accounts
            }]
          [guest-results
           {:feature-kw       :feature/sell-side-guest
            :state            ssg-state
            :filtered-guest-accounts filtered-ssg-accounts
            :title-kw         :company-workspace-users
            :guest-accounts   ssg-accounts
            :columns          (resolve-ssg-columns tenant state)
            :buy-side-guests  @bsg-accounts
            :sell-side-guests @ssg-accounts
            ;:lp-guests        @lpg-accounts
            }]
          #_[guest-results
           {:feature-kw       :feature/lp-reporting
            :state            lpg-state
            :filtered-guest-accounts filtered-lpg-accounts
            :title-kw         :lp-reporting-users
            :guest-accounts   lpg-accounts
            :columns          (resolve-lpg-columns tenant state)
            :buy-side-guests  @bsg-accounts
            :sell-side-guests @ssg-accounts
            :lp-guests        @lpg-accounts}]
          ])])))
